import { urlFile } from '../AxiosConfig';
import { useEffect, useState } from 'react';
import { EstabelecimentosService } from '../modules/estabelecimentos/system-admin/Service';
import { useEstabsContext } from '../contexts/EstabsContext';
import { useAppContext } from '../contexts/AppContext';

const estabelecimentosService = new EstabelecimentosService();

type LogoProps = {
    compact?: boolean;
    interactive?: boolean;
};

export const Logo = ({ compact = false, interactive = true }: LogoProps) => {
    const { estabSession, isClientOnly, clientEstab } = useEstabsContext();
    const { theme } = useAppContext();
    // Cliente ve a marca do estabelecimento escolhido no dispositivo; equipe ve a da sessao.
    const brandEstab = isClientOnly ? clientEstab : estabSession;
    const [title, setTitle] = useState(brandEstab?.name);
    const [image, setImage] = useState(brandEstab?.imageId);
    const [isLoading, setIsLoading] = useState(true);
    const [isImageLoading, setIsImageLoading] = useState(false);
    const logoSize = compact ? 36 : 120;
    const logoHeight = compact ? 36 : 40;

    useEffect(() => {
        let active = true;
        // Reinicia ao trocar de estabelecimento para nao manter a imagem do anterior.
        setTitle(brandEstab?.name);
        setImage(brandEstab?.imageId);

        if (!brandEstab?._id) {
            setIsLoading(false);
            return;
        }

        const fetchData = async () => {
            try {
                setIsLoading(true);
                const { data } = await estabelecimentosService.getInfoById(brandEstab._id);

                if (!active) return;
                setTitle(data?.name || brandEstab?.name);
                setImage(data?.imageId || '');
            } catch (error) {
                console.error('Erro ao carregar dados do estabelecimento:', error);
            } finally {
                if (active) setIsLoading(false);
            }
        };

        fetchData();
        return () => {
            active = false;
        };
    }, [brandEstab]);

    // Exibe spinner enquanto busca informações básicas
    if (isLoading) {
        return (
            <div
                className="flex items-center justify-center"
                style={{ width: `${logoSize}px`, height: `${logoHeight}px` }}
            >
                <span className="loading loading-spinner loading-sm"></span>
            </div>
        );
    }

    // Exibe imagem com loading enquanto ela é carregada
    if (image) {
        return (
            <div
                className="relative flex items-center justify-center"
                style={{ width: `${logoSize}px`, height: `${logoHeight}px` }}
            >
                {isImageLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-base-200/50 rounded">
                        <span className="loading loading-spinner loading-xs"></span>
                    </div>
                )}
                <img
                    src={`${urlFile}/${image}`}
                    alt="file preview"
                    style={{
                        width: '100%',
                        height: '100%',
                        maxWidth: `${logoSize}px`,
                        maxHeight: `${logoHeight}px`,
                        objectFit: 'contain',
                        opacity: isImageLoading ? 0 : 1,
                        transition: 'opacity 0.3s ease',
                    }}
                    onLoad={() => setIsImageLoading(false)}
                    onLoadStart={() => setIsImageLoading(true)}
                    onError={() => setIsImageLoading(false)}
                />
            </div>
        );
    }

    // Caso não tenha imagem, mostra o nome
    if (title) {
        if (compact) {
            const initials = title
                .split(' ')
                .filter((word: string) => Boolean(word))
                .slice(0, 2)
                .map((word: string) => word[0])
                .join('')
                .toUpperCase();

            return (
                <div className="flex items-center justify-center w-9 h-9 rounded-full bg-base-200 text-xs font-semibold">
                    {initials || 'SF'}
                </div>
            );
        }

        return (
            <div className={interactive ? 'btn btn-sm btn-ghost' : 'btn btn-sm btn-ghost cursor-default'}>
                <span className="text-xs">{title}</span>
            </div>
        );
    }

    // Logo padrão: versão com "Salon" branco no modo escuro; caminho absoluto para funcionar em rotas aninhadas.
    return (
        <img
            src={theme === 'dark' ? '/logo-light.png' : '/logo.png'}
            alt="SalonFlow"
            className={compact ? '' : 'mt-2'}
            style={{
                width: `${logoSize}px`,
                height: `${logoHeight}px`,
                objectFit: 'contain',
            }}
        />
    );
};
